// CSV helpers shared across all list pages.
// Excel-friendly: semicolon delimiter + UTF-8 BOM so umlauts and CHF amounts
// open correctly in German/Swiss Excel without a manual import wizard.

const DELIMITER = ';';
const BOM = '﻿';

function escapeCell(value) {
  if (value === null || value === undefined) {
    return '';
  }
  let str = String(value);
  /* Formel-Schutz (CWE-1236): eine Zelle, die mit = + - @ oder einem
     Tabulator beginnt, kann von einer Tabellenkalkulation als Formel
     gelesen werden. Ein vorangestellter Apostroph macht daraus Text.

     AUSNAHME: eine reine negative Zahl. "-500.00" ist keine Formel, und der
     Apostroph machte daraus Text - der Treuhänder bekam einen Minusbetrag,
     mit dem Excel nicht rechnen kann, und musste jede Spalte von Hand
     umwandeln. "-1+1" bleibt geschützt, weil es dem Muster nicht
     entspricht.

     Auch die Hausschreibweise gilt. formatters.js schreibt 1'234.50 fest, und
     genau die ist das Naheliegendste für eine Treuhänder-Ausfuhr. Ohne den
     Apostroph im Muster bekäme -1'234.50 den Schutz-Apostroph davor und
     wäre in Excel wieder Text - also genau der Fehler, den diese Ausnahme
     beseitigen sollte, nur eine Formatierung später. */
  const reineNegativeZahl =
    /^-\d+([.,]\d+)?$/.test(str) || /^-\d{1,3}(['’]\d{3})*([.,]\d+)?$/.test(str);
  if (!reineNegativeZahl && /^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }
  // Quote when the cell contains the delimiter, quotes, or newlines.
  if (str.includes(DELIMITER) || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Build a CSV string from rows.
 * @param {Array<object>} rows
 * @param {Array<{key: string, label: string, map?: (row) => any}>} columns
 */
export function toCsv(rows, columns) {
  const header = columns.map((c) => escapeCell(c.label)).join(DELIMITER);
  const body = rows.map((row) =>
    columns
      .map((c) => escapeCell(c.map ? c.map(row) : row[c.key]))
      .join(DELIMITER),
  );
  return BOM + [header, ...body].join('\r\n');
}

/** Trigger a browser download of `content` as `filename`. */
export function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Convenience: build + download in one call. */
export function exportCsv(filename, rows, columns) {
  downloadCsv(filename, toCsv(rows, columns));
}

/**
 * Parse a CSV string into objects keyed by the header row.
 * Handles quoted cells, escaped quotes (""), and both ; and , delimiters
 * (auto-detected from the header line). Returns { headers, rows }.
 */
export function parseCsv(text) {
  let input = text;
  if (input.charCodeAt(0) === 0xfeff) {
    input = input.slice(1); // strip BOM
  }
  // Detect delimiter from the first line: prefer ';' (Excel-DE), else ','.
  const firstLine = input.slice(0, input.indexOf('\n') === -1 ? input.length : input.indexOf('\n'));
  const delimiter = firstLine.split(';').length >= firstLine.split(',').length ? ';' : ',';

  const records = [];
  let field = '';
  let record = [];
  let inQuotes = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    if (inQuotes) {
      if (char === '"') {
        if (input[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      record.push(field);
      field = '';
    } else if (char === '\n') {
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else if (char !== '\r') {
      field += char;
    }
  }
  // Flush the trailing field/record (file may not end with a newline).
  if (field !== '' || record.length > 0) {
    record.push(field);
    records.push(record);
  }

  const nonEmpty = records.filter((r) => r.some((c) => c.trim() !== ''));
  if (nonEmpty.length === 0) {
    return { headers: [], rows: [] };
  }
  const headers = nonEmpty[0].map((h) => unescapeCell(h));
  const rows = nonEmpty.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = unescapeCell(cells[idx] ?? '');
    });
    return obj;
  });
  return { headers, rows };
}

/**
 * Macht den Formel-Schutz beim Einlesen wieder rückgängig.
 *
 * escapeCell stellt einer Zelle, die mit = + - @ oder einem Tabulator
 * beginnt, ein Apostroph voran. Ohne das Gegenstück hier war der Rundlauf
 * kaputt: eine Schweizer Telefonnummer "+41 79 123 45 67" kam als
 * "'+41 79 123 45 67" zurück - und JEDE weitere Ausfuhr hängte ein
 * weiteres Apostroph an. Die Anwendung bietet Ausfuhr und Einfuhr selbst
 * an; das war Datenverfall im eigenen Haus.
 *
 * Entfernt wird GENAU EIN führendes Apostroph, und nur, wenn danach ein
 * Zeichen steht, das den Schutz überhaupt ausgelöst hätte. Ein
 * niederländischer Name wie 't Hooft bleibt damit unangetastet - und
 * dasselbe Apostroph setzt Excel selbst, wenn jemand die Datei dort
 * bearbeitet.
 */
function unescapeCell(value) {
  const str = String(value).trim();
  if (str.length > 1 && str[0] === "'" && /^[=+\-@\t\r]/.test(str[1])) {
    return str.slice(1);
  }
  return str;
}
